import { useState, createContext, useContext, useCallback } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

export type TreeNode = {
  id: string;
  label: string;
  icon?: ReactNode;
  children?: TreeNode[];
  disabled?: boolean;
};

type TreeViewContextType = {
  expanded: Set<string>;
  selected: Set<string>;
  toggleExpand: (id: string) => void;
  toggleSelect: (id: string) => void;
  multiSelect: boolean;
  selectable: boolean;
  showCheckboxes: boolean;
};

const TreeViewContext = createContext<TreeViewContextType | null>(null);

function useTreeViewContext() {
  const context = useContext(TreeViewContext);
  if (!context) {
    throw new Error('TreeItem must be used within a TreeView');
  }
  return context;
}

export type TreeViewProps = {
  data: TreeNode[];
  defaultExpanded?: string[];
  defaultSelected?: string[];
  expanded?: string[];
  selected?: string[];
  onExpandedChange?: (expanded: string[]) => void;
  onSelectedChange?: (selected: string[]) => void;
  multiSelect?: boolean;
  selectable?: boolean;
  showCheckboxes?: boolean;
  className?: string;
};

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={clsx('size-4 transition-transform', isOpen && 'rotate-90')}
  >
    <path
      fillRule="evenodd"
      d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
      clipRule="evenodd"
    />
  </svg>
);

function TreeItem({ node, level = 0 }: { node: TreeNode; level?: number }) {
  const { expanded, selected, toggleExpand, toggleSelect, selectable, showCheckboxes } =
    useTreeViewContext();
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.has(node.id);
  const isSelected = selected.has(node.id);

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-1 py-1.5 px-2 rounded-md cursor-pointer transition-colors',
          isSelected
            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
            : 'hover:bg-gray-100 dark:hover:bg-dark-700',
          node.disabled && 'opacity-50 cursor-not-allowed'
        )}
        style={{ paddingLeft: `${level * 20 + 8}px` }}
        onClick={() => {
          if (node.disabled) return;
          if (hasChildren) {
            toggleExpand(node.id);
          }
          if (selectable) {
            toggleSelect(node.id);
          }
        }}
      >
        <span className="w-5 flex items-center justify-center shrink-0 text-gray-400 dark:text-dark-400">
          {hasChildren && <ChevronIcon isOpen={isExpanded} />}
        </span>
        {showCheckboxes && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelect(node.id)}
            disabled={node.disabled}
            className="size-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500 dark:border-dark-500"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {node.icon && (
          <span className="shrink-0 text-gray-400 dark:text-dark-400">{node.icon}</span>
        )}
        <span
          className={clsx(
            'text-sm',
            isSelected ? 'font-medium' : 'text-gray-700 dark:text-dark-200'
          )}
        >
          {node.label}
        </span>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children!.map((child) => (
            <TreeItem key={child.id} node={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function TreeView({
  data,
  defaultExpanded = [],
  defaultSelected = [],
  expanded: controlledExpanded,
  selected: controlledSelected,
  onExpandedChange,
  onSelectedChange,
  multiSelect = false,
  selectable = true,
  showCheckboxes = false,
  className,
}: TreeViewProps) {
  const [internalExpanded, setInternalExpanded] = useState<Set<string>>(new Set(defaultExpanded));
  const [internalSelected, setInternalSelected] = useState<Set<string>>(new Set(defaultSelected));

  const isExpandedControlled = controlledExpanded !== undefined;
  const isSelectedControlled = controlledSelected !== undefined;

  const expanded = isExpandedControlled ? new Set(controlledExpanded) : internalExpanded;
  const selected = isSelectedControlled ? new Set(controlledSelected) : internalSelected;

  const toggleExpand = useCallback(
    (id: string) => {
      const newExpanded = new Set(expanded);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      if (!isExpandedControlled) {
        setInternalExpanded(newExpanded);
      }
      onExpandedChange?.(Array.from(newExpanded));
    },
    [expanded, isExpandedControlled, onExpandedChange]
  );

  const toggleSelect = useCallback(
    (id: string) => {
      let newSelected: Set<string>;
      if (multiSelect) {
        newSelected = new Set(selected);
        if (newSelected.has(id)) {
          newSelected.delete(id);
        } else {
          newSelected.add(id);
        }
      } else {
        newSelected = selected.has(id) ? new Set() : new Set([id]);
      }
      if (!isSelectedControlled) {
        setInternalSelected(newSelected);
      }
      onSelectedChange?.(Array.from(newSelected));
    },
    [selected, multiSelect, isSelectedControlled, onSelectedChange]
  );

  return (
    <TreeViewContext.Provider
      value={{
        expanded,
        selected,
        toggleExpand,
        toggleSelect,
        multiSelect,
        selectable,
        showCheckboxes,
      }}
    >
      <div className={clsx('py-2', className)}>
        {data.map((node) => (
          <TreeItem key={node.id} node={node} />
        ))}
      </div>
    </TreeViewContext.Provider>
  );
}

TreeView.displayName = 'TreeView';

export { TreeView };
